import { Input } from '@tempo-io/sui-input';
import { toast } from '@tempo-io/sui-sonner';
import debounce from 'lodash.debounce';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ItemsApi } from '@/common/api/items';
import type { Item } from '@/common/api/types/items';
import type { ItemTypeMap } from '@/common/api/types/itemType';
import { BaseDialog } from '@/common/components/base-dialog/BaseDialog';
import { ItemTypeIcon } from '@/common/components/ui/item-type/itemTypeIcon';

const SEARCH_RESULTS_LIMIT = 50;
const DEBOUNCE_DELAY_MS = 300;
const MIN_SEARCH_LENGTH = 2;

interface ItemSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemTypesById: ItemTypeMap;
  excludedItemIds?: readonly number[];
  selectedItemId?: number;
  onSave: (itemId: number) => void;
  onClear?: () => void;
  title: string;
}

export const ItemSelectionDialog = ({
  open,
  onOpenChange,
  itemTypesById,
  excludedItemIds,
  selectedItemId,
  onSave,
  onClear,
  title,
}: ItemSelectionDialogProps) => {
  const { t } = useTranslation();
  const [searchInputValue, setSearchInputValue] = React.useState('');
  const [results, setResults] = React.useState<readonly Item[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const openRef = React.useRef(open);
  openRef.current = open;
  const requestIdRef = React.useRef(0);

  const searchRef = React.useRef(
    debounce(async (input: string) => {
      const requestId = ++requestIdRef.current;
      const isCurrent = () => openRef.current && requestId === requestIdRef.current;
      const trimmed = input.trim();
      if (trimmed.length < MIN_SEARCH_LENGTH) {
        if (!isCurrent()) return;
        setResults([]);
        setIsLoading(false);
        return;
      }
      try {
        const response = await ItemsApi.getItems({
          filter: { name: { value: trimmed, operator: 'startsWithIgnoreCase' } },
          order: 'name',
          limit: SEARCH_RESULTS_LIMIT,
          hierarchical: false,
        });
        if (!isCurrent()) return;
        setResults(response.data);
      } catch {
        if (!isCurrent()) return;
        toast.error(t(($) => $.adaptivePlanner.itemPanel.customFields.itemPicker.loadError));
        setResults([]);
      } finally {
        if (isCurrent()) setIsLoading(false);
      }
    }, DEBOUNCE_DELAY_MS),
  );

  React.useEffect(() => {
    const ref = searchRef.current;
    return () => {
      ref.cancel();
    };
  }, []);

  React.useEffect(() => {
    if (!open) {
      requestIdRef.current++;
    }
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      requestIdRef.current++;
      searchRef.current.cancel();
      setSearchInputValue('');
      setResults([]);
      setIsLoading(false);
    }
    onOpenChange(next);
  };

  const handleSearchChange = (value: string) => {
    setSearchInputValue(value);
    if (value.trim().length < MIN_SEARCH_LENGTH) {
      setResults([]);
      setIsLoading(false);
      searchRef.current.cancel();
      return;
    }
    setIsLoading(true);
    searchRef.current(value);
  };

  const visibleResults = results.filter((item) => !excludedItemIds?.includes(item.id));

  const handlePick = (itemId: number) => {
    onSave(itemId);
    handleOpenChange(false);
  };

  const emptyMessage =
    searchInputValue.trim().length < MIN_SEARCH_LENGTH
      ? t(($) => $.adaptivePlanner.itemPanel.customFields.itemPicker.minCharacters, {
          count: MIN_SEARCH_LENGTH,
        })
      : isLoading
        ? null
        : t(($) => $.adaptivePlanner.itemPanel.customFields.itemPicker.noResults);

  return (
    <BaseDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      className="z-1400! h-4/6 overflow-hidden flex flex-col"
      additionalAction={
        onClear && selectedItemId
          ? {
              label: t(($) => $.adaptivePlanner.itemPanel.userField.clear),
              onClick: () => {
                onClear();
                handleOpenChange(false);
              },
              'data-testid': 'item-clear-button',
            }
          : undefined
      }
    >
      <div className="flex flex-col gap-4 min-h-0 flex-1">
        <Input
          type="text"
          value={searchInputValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder={t(($) => $.adaptivePlanner.itemPanel.customFields.itemPicker.placeholder)}
          autoFocus
        />

        <div className="flex flex-col gap-1 overflow-auto flex-1 min-h-0">
          {visibleResults.length === 0 && emptyMessage && (
            <span className="text-sm text-text-neutral-secondary p-3">{emptyMessage}</span>
          )}
          {visibleResults.map((item) => {
            const itemType = itemTypesById[item.itemTypeId];
            return (
              <button
                key={item.id}
                type="button"
                data-testid={`item-option-${item.id}`}
                onClick={() => handlePick(item.id)}
                className="flex items-center gap-2 p-3 text-left text-sm hover:bg-bg-neutral-subtle/50 transition-colors rounded-md"
              >
                {itemType && <ItemTypeIcon itemType={itemType} />}
                <span className="truncate">{item.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </BaseDialog>
  );
};
